import React, { useContext, useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import cn from 'classnames'

import Modal from '../Common/Modal'
import FormError from './FormError'
import Button from '../button/button'
import Input from '../Common/Input'
import Checkbox from '../Common/Checkbox'
import Typography from '../Common/Typography'

import { useTranslations } from '../../hooks/use-translations'
import { GlobalContext } from '../../context/GlobalContext'
import { getMailUrl, toLink } from '../../utils'
import { AppRegionEnum } from '../../types/app-region.enum'
import { emailRegex, FormState, nameSurnameRegex } from './config'
import { IContactFields } from './types'
import { appendEmailjsFields } from './helpers'

interface IProps {
  title?: string
  submitBtnText?: string
  onSuccess?: () => void
  onClose?: () => void
}

const TryForFreeForm: React.FC<IProps> = props => {
  const { location, region } = useContext(GlobalContext)
  const { t } = useTranslations()
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<IContactFields>({
    defaultValues: {
      name: '',
      company: '',
      email: '',
      phone: '',
    },
    mode: 'all',
  })

  const [state, setState] = useState<FormState>(FormState.Ready)
  const [checkPersonal, setCheckPersonal] = useState(false)

  const {
    title = t('Submit your application'),
    submitBtnText = t('Send'),
    onClose,
    onSuccess,
  } = props
  const hostName = location?.hostname || ''

  const onSubmit: SubmitHandler<IContactFields> = data => {
    setState(FormState.Processing)
    const mailUrl = getMailUrl(hostName, region as AppRegionEnum)
    const formData = new FormData()
    formData.append('title', `${t('message')} ${hostName}: ${title}`)
    formData.append('name', data.name)
    formData.append('email', data.email)
    formData.append('company', '')
    formData.append('phone', '')
    formData.append('region', region)
    if (region === 'us') {
      appendEmailjsFields(formData)
    }

    fetch(mailUrl, {
      body: formData,
      method: 'POST',
    })
      .then((r: Response) => {
        if (r.ok) {
          setState(FormState.Done)
          if (onSuccess) {
            onSuccess()
            handleClose()
          }
        } else {
          console.warn(r)
          setState(FormState.Error)
        }
      })
      .catch(e => {
        console.error(e)
        setState(FormState.Error)
      })
  }

  const handleClose = () => {
    if (state === FormState.Processing) {
      return
    }
    onClose?.()
  }

  if (state === FormState.Error) {
    return <FormError onCLose={handleClose} />
  }

  if (state === FormState.Done) {
    return (
      <Modal onClose={handleClose} title={title}>
        <Typography variant="body1" size={18} mb={32}>
          Thank you! Kindly follow the link for credentials and instructions.
        </Typography>
        <div className="flex justify-center">
          <Button
            isFullWidth
            onClick={() =>
              toLink(
                'https://wiki.signax.io/app/page/1i-Yq4tHAM7xTAv-Ps_KrYOlAWYmEVbRH?p=16QE_sDsILkr3fZjUyIvlqkMNT4DouXIy'
              )
            }
          >
            Get Access
          </Button>
        </div>
      </Modal>
    )
  }

  return (
    <Modal onClose={handleClose} title={title}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="form is-block"
        autoComplete="off"
      >
        <fieldset disabled={state === 'processing'}>
          <Controller
            name="name"
            control={control}
            rules={{ required: true, pattern: nameSurnameRegex }}
            render={({ field }) => (
              <Input
                {...field}
                label={t('Your full name')}
                hasError={!!errors.name}
                className="mb-2"
                type="text"
                fullWidth
              />
            )}
          />
          <Controller
            name="email"
            control={control}
            rules={{ required: true, pattern: emailRegex }}
            render={({ field }) => (
              <Input
                {...field}
                label={t('E-Mail')}
                type="email"
                hasError={!!errors.email}
                className="mb-2"
                fullWidth
              />
            )}
          />
          <div className=" flex flex-col mt-8">
            <div className="flex items-center">
              <Checkbox
                id="personal"
                checked={checkPersonal}
                onChange={checked => setCheckPersonal(checked)}
              />
              <label>
                <Typography variant="body2" style={{ fontSize: 16 }}>
                  {`${t('Agreement')} `}
                  <a
                    className="has-text-primary"
                    href="/privacy"
                    target="_blank"
                  >{`${t('Personal Data')}.`}</a>
                </Typography>
              </label>
            </div>
            <Button
              role="submit"
              style={{ marginTop: 16 }}
              className={cn(state === 'processing' && 'is-loading')}
              disabled={!isValid || !checkPersonal || state !== 'ready'}
              isFullWidth
              isBoxShadow={false}
            >
              {submitBtnText}
            </Button>
          </div>
        </fieldset>
      </form>
    </Modal>
  )
}

export default TryForFreeForm
